import type { TFunction } from 'i18next';

import { errorCodeOf, httpStatusOf } from '../../api/errors';

/**
 * Error handling shared by every «Площадка» screen (AUT-542).
 *
 * «Раздел выключен» is ONLY 404 with `code === 'not_found'` — what the
 * feature-flag guards answer. `specialist_profile_not_found`,
 * `car_listing_not_found`, `request_not_found` mean «this record is gone» and
 * stay ordinary errors.
 */
export const isSectionDisabledError = (err: unknown): boolean =>
  httpStatusOf(err) === 404 && errorCodeOf(err) === 'not_found';

const KNOWN_CODES = new Set([
  'content_report_closed',
  'listing_status_invalid',
  'listing_report_in_use',
  'listing_placement_exists',
  'listing_placement_not_found',
  'listing_placement_term_invalid',
  'org_self_brand_from_profile',
  'org_self_owner_fixed',
  'org_self_owner_remove_forbidden',
]);

/** i18n key for a write failure: known 409 codes have their own text, the rest — the generic one. */
export const marketplaceErrorKey = (err: unknown): string => {
  const code = errorCodeOf(err);
  if (code !== null && KNOWN_CODES.has(code)) return `marketplace.errors.${code}`;
  return 'marketplace.errors.generic';
};

/**
 * The translated text shown under the action. The generic text carries the
 * server's code (or the HTTP status when there is no code); a failure without
 * a response (network) gets the bare «Не получилось».
 */
export const marketplaceErrorMessage = (err: unknown, t: TFunction): string => {
  const key = marketplaceErrorKey(err);
  if (key !== 'marketplace.errors.generic') return t(key);
  const code = errorCodeOf(err) ?? httpStatusOf(err)?.toString() ?? null;
  return code === null ? t('marketplace.errors.genericNoCode') : t(key, { code });
};
